import { landingApiRequest } from '@/lib/landing-api'
import type { LandingAchievementId } from '@/lib/landing-achievements'
import { ACHIEVEMENT_DEFINITIONS } from '@/lib/landing-achievement-definitions'
import type { AvatarConfig } from '@/features/donorAvatar/avatar-catalog'
import type { LandingCampaign, LandingInstitution } from '@/types/landing'
import { isSafeHttpUrl, stripSensitiveQueryFromUrl } from '@/lib/campaign-page-url'
import { resolveCampaignViewPageUrl } from '@/lib/campaign-view-page-url'
import { resolveLandingAccountTitle } from '@/lib/landing-account-visual'
import { isCampaignOpenForSales } from '@/lib/campaign-sales'

export type ParticipantShareSnapshot = {
  campaign_id: number
  participant: {
    display_name: string
    avatar: AvatarConfig | null
    profile_photo_url: string | null
  }
  numbers_count: number
  paid_purchase_count: number
  top_rank: number | null
  achievement_ids: LandingAchievementId[]
}

export type ParticipantShareInput = {
  campaign: LandingCampaign
  institution: LandingInstitution
  origin: string
  officialShareUrl?: string
}

export type ParticipantShareModel = ParticipantShareInput & {
  snapshot: ParticipantShareSnapshot
  url: string
  title: string
  text: string
  participation: string
  invitation: string
  donationOnly: boolean
  filename: string
}

export function fetchParticipantShareSnapshot(campaignId: number, signal?: AbortSignal) {
  return landingApiRequest<ParticipantShareSnapshot>(
    `/api/me/campaigns/${campaignId}/share-card`,
    {
      method: 'GET',
      signal,
    }
  )
}

export function buildParticipantShareUrl(
  campaign: LandingCampaign,
  origin: string,
  officialShareUrl?: string
): string {
  const destination = resolveCampaignViewPageUrl({
    slug: campaign.slug,
    publicPageUrl: campaign.public_page_url,
  })
  const fallback = new URL(`/campanha/${encodeURIComponent(campaign.slug ?? '')}`, origin)
  const candidate = new URL(officialShareUrl || destination || fallback.href, origin)
  const url = isSafeHttpUrl(candidate.href)
    ? new URL(stripSensitiveQueryFromUrl(candidate.href))
    : fallback
  url.hash = ''
  url.searchParams.set('utm_source', 'compartilhamento')
  url.searchParams.set('utm_medium', 'social')
  url.searchParams.set('utm_campaign', campaign.slug || String(campaign.id))
  url.searchParams.set('utm_content', 'card_participante')
  return url.href
}

export function buildParticipantShareModel(
  input: ParticipantShareInput,
  snapshot: ParticipantShareSnapshot
): ParticipantShareModel {
  if (snapshot.campaign_id !== input.campaign.id) throw new Error('Campanha inválida.')
  const title = resolveLandingAccountTitle(input.campaign)
  const donationOnly = input.campaign.generation_status === 'donation_only'
  const paid = snapshot.paid_purchase_count > 0
  const participation = paid
    ? donationOnly
      ? 'Eu apoiei esta campanha.'
      : 'Eu garanti meus números da sorte.'
    : snapshot.numbers_count > 0
      ? 'Meus números da sorte já estão aqui.'
      : 'Eu conheci esta campanha.'
  const invitation = !isCampaignOpenForSales(input.campaign)
    ? 'Conheça esta campanha'
    : donationOnly
      ? 'Venha apoiar também'
      : 'Garanta seus números também'
  const url = buildParticipantShareUrl(
    input.campaign,
    input.origin,
    input.officialShareUrl
  )
  const rank =
    Number.isInteger(snapshot.top_rank) &&
    snapshot.top_rank! >= 1 &&
    snapshot.top_rank! <= 10
      ? snapshot.top_rank
      : null
  const achievements = ACHIEVEMENT_DEFINITIONS.filter((badge) =>
    snapshot.achievement_ids.includes(badge.id)
  )
  const text = [
    `${participation} ✨\n${title}`,
    rank
      ? `Estou no Top ${rank} de ${donationOnly ? 'apoiadores' : 'compradores'} da campanha!`
      : '',
    achievements.length
      ? `Minhas conquistas: ${achievements.map((badge) => badge.title).join(', ')}.`
      : '',
    `${invitation}!\n${url}`,
  ]
    .filter(Boolean)
    .join('\n\n')
  const slug = (input.campaign.slug || 'campanha')
    .replace(/[^a-zA-Z0-9_-]/g, '-')
    .slice(0, 80)
  return {
    ...input,
    snapshot: { ...snapshot, top_rank: rank },
    url,
    title,
    text,
    participation,
    invitation,
    donationOnly,
    filename: `minha-jornada-${slug}.png`,
  }
}

export function canShareParticipantFile(file: File): boolean {
  try {
    return (
      typeof navigator.share === 'function' &&
      navigator.canShare?.({ files: [file] }) === true
    )
  } catch {
    return false
  }
}

/** Called directly from the ready preview's button, preserving user activation. */
export async function shareParticipantFile(
  file: File,
  model: ParticipantShareModel
): Promise<'opened' | 'cancelled'> {
  try {
    await navigator.share({ files: [file], title: model.title, text: model.text })
    return 'opened'
  } catch (error) {
    if (
      typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      error.name === 'AbortError'
    )
      return 'cancelled'
    throw error
  }
}
